<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { Check, X, Clock, AlertTriangle } from 'lucide-vue-next'
import apiHelper from '@/utils/apiHelper'
import { API_URL } from '@/constants/api'
import commonUtil from '@/utils/commonUtil'
import type { DataDownloadHistoryResponse } from '@/types/data'
import { useTableTooltip } from '@/utils/useTableTooltip'
import TableTooltip from '@/components/TableTooltip.vue'
import DataTable from '@/components/DataTable.vue'
import SkeletonLines from '@/components/SkeletonLines.vue'
import { useDownloadStore } from '@/stores'

const {
  hoveredTooltipText,
  showTableTooltip,
  tableTooltipX,
  tableTooltipY,
  handleTableTooltipMouseMove,
  handleCellMouseEnter,
  handleCellMouseLeave,
  handleTooltipMouseEnter,
  handleTooltipMouseLeave,
} = useTableTooltip()

// dataNo가 주어지면 그 데이터에 대한(그리고 로그인 사용자 본인의) 이력만, 없으면 로그인
// 사용자 본인의 전체 다운로드 이력을 보여준다. 두 경우 모두 이미 그 화면 어딘가(데이터 상세
// 팝업 헤더 등)에 프로젝트/세션/데이터명이 고정 표시된다는 전제 하에, 데이터 컨텍스트가
// 있을 때만 "데이터" 컬럼을 생략한다 — 값이 바뀌지 않는 정보를 행마다 반복하지 않기 위함이지,
// 정보를 감추는 게 아니다(docs/download-limits-history-plan.md 4-2 참고).
const props = defineProps<{
  dataNo?: number
}>()

// 서버는 최대 200건까지 허용한다(MaxDownloadHistoryLimit). 화면은 공용 DataTable의
// 자체 페이지네이션으로 넘겨보므로 "더 보기" 없이 최근 기록을 한 번에 받아온다.
const FETCH_LIMIT = 100

const rows = ref<DataDownloadHistoryResponse[]>([])
const loading = ref(false)
const loadError = ref(false)

const isDataScoped = computed(() => props.dataNo !== undefined && props.dataNo !== null)

function historyUrl(): string {
  return isDataScoped.value ? API_URL.DATA.DOWNLOAD_HISTORY(props.dataNo as number) : API_URL.DOWNLOAD.HISTORY
}

async function load() {
  loading.value = true
  loadError.value = false
  const res = await apiHelper.get<DataDownloadHistoryResponse[]>(historyUrl(), { limit: FETCH_LIMIT, offset: 0 })
  if (!res || res.errorCode !== 0 || !res.data) {
    loadError.value = true
    rows.value = []
  } else {
    rows.value = res.data
  }
  loading.value = false
}

function formatDateTime(iso: string): string {
  return commonUtil.formatDateTime(iso, '')
}

const STATUS_LABEL: Record<string, string> = {
  DOWNLOADING: '다운로드 중',
  DONE: '완료',
  ERROR: '실패',
  CANCELLED: '취소됨',
}

const STATUS_STYLE: Record<string, { color: string; background: string }> = {
  DONE: { color: '#059669', background: 'rgba(5, 150, 105, 0.1)' },
  ERROR: { color: '#DC2626', background: 'rgba(220, 38, 38, 0.1)' },
  CANCELLED: { color: '#6B7280', background: 'rgba(107, 114, 128, 0.1)' },
  DOWNLOADING: { color: '#2563EB', background: 'rgba(37, 99, 235, 0.1)' },
}

// DataTable은 headers 배열 + cell-<key> 슬롯으로 렌더링을 위임받는 공용 컴포넌트 —
// 데이터/프로젝트 목록 등 앱 다른 화면과 동일한 헤더 스타일·페이지네이션을 그대로 물려받는다.
const headers = computed(() => {
  const base = [{ label: '일시', key: 'downloadDate', width: '150px', align: 'left' as const, sortKey: null }]
  if (!isDataScoped.value) {
    base.push({ label: '데이터', key: 'dataName', width: 'auto', align: 'left' as const, sortKey: null })
  }
  base.push(
    { label: '파일명', key: 'fileName', width: 'auto', align: 'left' as const, sortKey: null },
    { label: '크기', key: 'fileSize', width: '90px', align: 'right' as const, sortKey: null },
    { label: '상태', key: 'status', width: '110px', align: 'left' as const, sortKey: null },
  )
  return base
})

onMounted(load)

// 다운로드 시작/완료/실패/취소가 서버에 반영될 때마다 다시 불러온다. 다운로드 관리 모달을
// 열어 둔 채 다운로드가 끝나도 이력의 "다운로드 중"이 바로 "완료"로 바뀌도록 하기 위함.
const downloadStore = useDownloadStore()
watch(() => downloadStore.historyVersion, load)

// 부모(다운로드 관리 모달 등)가 새 다운로드 완료 시점에 이 테이블을 갱신할 수 있게 노출
defineExpose({ refresh: load })
</script>

<template>
  <div>
    <SkeletonLines v-if="loading" variant="table" :lines="6" :columns="4" />
    <p v-else-if="loadError" class="px-4 py-8 text-center text-sm text-rose-500">이력을 불러오지 못했습니다.</p>
    <DataTable
      v-else
      :headers="headers"
      :items="rows"
      :page-size="6"
      empty-message="다운로드 이력이 없습니다"
      empty-description="파일을 다운로드하면 여기에 기록이 표시됩니다."
    >
      <template #cell-downloadDate="{ item }">
        <span class="cell-text whitespace-nowrap">{{ formatDateTime(item.downloadDate) }}</span>
      </template>

      <template v-if="!isDataScoped" #cell-dataName="{ item }">
        <div class="max-w-[220px]">
          <p
            class="cell-text font-semibold truncate"
            @mouseenter="handleCellMouseEnter($event, item.dataName)"
            @mouseleave="handleCellMouseLeave"
            @mousemove="handleTableTooltipMouseMove"
          >
            {{ item.dataName }}
          </p>
          <p
            class="text-[11px] text-slate-400 truncate"
            @mouseenter="handleCellMouseEnter($event, `${item.projectName} › ${item.sessionName}`)"
            @mouseleave="handleCellMouseLeave"
            @mousemove="handleTableTooltipMouseMove"
          >
            {{ item.projectName }} › {{ item.sessionName }}
          </p>
        </div>
      </template>

      <template #cell-fileName="{ item }">
        <span
          class="cell-text block truncate max-w-[220px]"
          @mouseenter="handleCellMouseEnter($event, item.fileName)"
          @mouseleave="handleCellMouseLeave"
          @mousemove="handleTableTooltipMouseMove"
        >
          {{ item.fileName || '-' }}
        </span>
      </template>

      <template #cell-fileSize="{ item }">
        <span class="cell-text whitespace-nowrap">{{ item.fileSize ? commonUtil.formatBytes(item.fileSize, 1) : '-' }}</span>
      </template>

      <template #cell-status="{ item }">
        <span
          class="status-pill"
          :style="{ color: STATUS_STYLE[item.status]?.color, backgroundColor: STATUS_STYLE[item.status]?.background }"
        >
          <Check v-if="item.status === 'DONE'" :size="11" :stroke-width="2.5" />
          <AlertTriangle v-else-if="item.status === 'ERROR'" :size="11" :stroke-width="2.5" />
          <X v-else-if="item.status === 'CANCELLED'" :size="11" :stroke-width="2.5" />
          <Clock v-else :size="11" :stroke-width="2.5" />
          {{ STATUS_LABEL[item.status] || item.status }}
        </span>
      </template>
    </DataTable>

    <TableTooltip
      :show="showTableTooltip"
      :text="hoveredTooltipText"
      :x="tableTooltipX"
      :y="tableTooltipY"
      @mouseenter="handleTooltipMouseEnter"
      @mouseleave="handleTooltipMouseLeave"
    />
  </div>
</template>

<style scoped>
.cell-text {
  font-size: 12px;
  color: #374151;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 20px;
  white-space: nowrap;
}
</style>
